import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchComboProducts,
  fetchCustomerEnrollment,
  fetchEnrollmentRedemptions,
  fetchIndividualProducts,
  redeemEnrollmentProducts,
} from "../customerSchemeApi";
import "../CustomerScheme.css";

const getMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.msg || fallback;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeEnrollments = (raw) => {
  const source = raw?.data?.data || raw?.data || [];
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.enrollments)) return source.enrollments;
  if (source?.enrollment) return [source.enrollment];
  return source ? [source] : [];
};

const getProductPrice = (product) => {
  if (!product) return 0;
  return toNumber(
    product.price ||
      product.finalAmount ||
      product.scheme_price ||
      product.total_amount ||
      product.amount
  );
};

const getOpenValueMeta = (enrollment) => {
  const paymentPlan = enrollment?.paymentPlan || {};
  return {
    productType: String(enrollment?.productType || enrollment?.planType || "").toUpperCase(),
    canRedeem: Boolean(paymentPlan?.canRedeem ?? enrollment?.canRedeem),
    availableCredit: toNumber(paymentPlan?.availableCredit ?? enrollment?.availableCredit),
    redeemedAmount: toNumber(paymentPlan?.redeemedAmount ?? enrollment?.redeemedAmount),
    totalAmount: toNumber(enrollment?.totalAmount),
    redemptionStatus: String(
      paymentPlan?.redemptionStatus || enrollment?.redemptionStatus || "NOT_AVAILABLE"
    ).toUpperCase(),
  };
};

const SavingsRedemptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enrollmentIdFromQuery = searchParams.get("enrollmentId") || "";

  const authData = useMemo(
    () => JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}"),
    []
  );
  const customerMasterId = authData?.customerMasterId;
  const customerId = authData?.customerId;
  const customerPhone = authData?.phone;
  const customerMobile = authData?.mobile;
  const identityCandidates = useMemo(
    () =>
      Array.from(
        new Set([
          customerMasterId,
          customerId,
          customerPhone,
          customerMobile,
        ].filter(Boolean))
      ),
    [customerMasterId, customerId, customerPhone, customerMobile]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(enrollmentIdFromQuery);
  const [catalog, setCatalog] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [selectedType, setSelectedType] = useState("INDIVIDUAL");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [cart, setCart] = useState([]);
  const [redemptionHistory, setRedemptionHistory] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (identityCandidates.length === 0) {
        setError("Customer session missing. Please login again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        let parsedEnrollments = [];
        for (const candidate of identityCandidates) {
          try {
            const response = await fetchCustomerEnrollment(candidate);
            parsedEnrollments = normalizeEnrollments(response);
            if (parsedEnrollments.length > 0) break;
          } catch (innerErr) {
            void innerErr;
          }
        }

        const [comboResponse, individualResponse] = await Promise.all([
          fetchComboProducts(),
          fetchIndividualProducts(),
        ]);

        const comboList = (comboResponse?.data?.data || []).map((item) => ({
          type: "COMBO",
          code: item?.comboCode || item?.combo_code || "",
          name: item?.comboName || item?.description || item?.comboCode || "Combo",
          price: getProductPrice(item),
        }));

        const individualList = (individualResponse?.data?.data || []).map((item) => ({
          type: "INDIVIDUAL",
          code: item?.productCode || item?.model_code || "",
          name: item?.productName || item?.product_name || item?.productCode || "Product",
          price: getProductPrice(item),
        }));

        const nextCatalog = [...comboList, ...individualList].filter((item) => item.code);
        setCatalog(nextCatalog);
        setEnrollments(parsedEnrollments);

        const fallbackId = String(parsedEnrollments[0]?._id || parsedEnrollments[0]?.id || "");
        setSelectedEnrollmentId((prev) => prev || fallbackId);
      } catch (err) {
        setError(getMessage(err, "Unable to load redemption page."));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [identityCandidates]);

  const selectedEnrollment = useMemo(
    () =>
      enrollments.find(
        (item) => String(item?._id || item?.id || "") === String(selectedEnrollmentId)
      ) || null,
    [enrollments, selectedEnrollmentId]
  );

  const openValueMeta = useMemo(
    () => getOpenValueMeta(selectedEnrollment),
    [selectedEnrollment]
  );

  const isOpenValuePlan = openValueMeta.productType === "OPEN_VALUE";

  useEffect(() => {
    const loadHistory = async () => {
      if (!selectedEnrollmentId) {
        setRedemptionHistory([]);
        return;
      }

      try {
        const response = await fetchEnrollmentRedemptions(selectedEnrollmentId);
        const list = response?.data?.data || response?.data || [];
        setRedemptionHistory(Array.isArray(list) ? list : []);
      } catch (err) {
        void err;
        setRedemptionHistory([]);
      }
    };

    loadHistory();
  }, [selectedEnrollmentId]);

  const filteredCatalog = useMemo(
    () => catalog.filter((item) => item.type === selectedType),
    [catalog, selectedType]
  );

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + toNumber(item.price) * toNumber(item.quantity), 0),
    [cart]
  );

  const topUpEstimate = Math.max(cartTotal - openValueMeta.availableCredit, 0);

  const addToCart = () => {
    const code = String(selectedCode || "").trim();
    const qty = Math.max(Number(quantity || 1), 1);
    if (!code) {
      setError("Select a product code to add.");
      return;
    }

    const found = catalog.find((item) => item.code === code && item.type === selectedType);
    if (!found) {
      setError("Unable to find selected product.");
      return;
    }

    setError("");
    setCart((prev) => {
      const existingIndex = prev.findIndex((item) => item.code === code && item.type === selectedType);
      if (existingIndex === -1) {
        return [...prev, { ...found, quantity: qty }];
      }

      return prev.map((item, index) =>
        index === existingIndex ? { ...item, quantity: toNumber(item.quantity) + qty } : item
      );
    });
  };

  const updateCartQuantity = (index, nextQty) => {
    const normalizedQty = Math.max(Number(nextQty || 1), 1);
    setCart((prev) => prev.map((item, idx) => (idx === index ? { ...item, quantity: normalizedQty } : item)));
  };

  const removeCartItem = (index) => {
    setCart((prev) => prev.filter((_, idx) => idx !== index));
  };

  const submitRedemption = async () => {
    if (!selectedEnrollmentId) {
      setError("Select an enrollment first.");
      return;
    }

    if (!isOpenValuePlan) {
      setError("Redemption is only available for Savings Scheme enrollments.");
      return;
    }

    if (!openValueMeta.canRedeem) {
      setError("Redemption is not available yet for this enrollment.");
      return;
    }

    if (cart.length === 0) {
      setError("Add at least one product to redeem.");
      return;
    }

    if (cartTotal > openValueMeta.availableCredit) {
      setError("Cart amount exceeds available credit. Cash top-up is not accepted by current API yet.");
      return;
    }

    try {
      setSubmitLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        enrollmentId: selectedEnrollmentId,
        redeemedBy: authData?.customerName || "CUSTOMER",
        notes: notes.trim(),
        products: cart.map((item) => ({
          type: item.type,
          code: item.code,
          quantity: Math.max(Number(item.quantity || 1), 1),
        })),
      };

      const response = await redeemEnrollmentProducts(payload);
      const responseData = response?.data?.data || response?.data || {};
      const updatedEnrollment = responseData?.enrollment;
      const redemption = responseData?.redemption;

      if (updatedEnrollment?._id || updatedEnrollment?.id) {
        const updatedId = String(updatedEnrollment?._id || updatedEnrollment?.id || "");
        setEnrollments((prev) =>
          prev.map((item) =>
            String(item?._id || item?.id || "") === updatedId ? updatedEnrollment : item
          )
        );
      }

      if (redemption) {
        setRedemptionHistory((prev) => [redemption, ...prev]);
      }

      setCart([]);
      setSuccess("Redemption completed successfully.");
      setNotes("");
    } catch (err) {
      setError(getMessage(err, "Unable to submit redemption."));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-header">
          <div>
            <h2>Redeem Products</h2>
            <p className="customer-subtitle">Use Savings Scheme credit to redeem combo or individual products.</p>
          </div>
          <div className="customer-row">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/dashboard")}>Back to Dashboard</button>
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/payment")}>Go to Payment</button>
          </div>
        </div>

        {loading ? (
          <div className="customer-card">Loading redemption details...</div>
        ) : (
          <>
            <div className="customer-card customer-form">
              <div className="customer-field">
                <label>Select Enrollment</label>
                <select
                  value={String(selectedEnrollmentId || "")}
                  onChange={(e) => setSelectedEnrollmentId(e.target.value)}
                >
                  {enrollments.length === 0 ? (
                    <option value="">No enrollment found</option>
                  ) : (
                    enrollments.map((item, idx) => {
                      const value = String(item?._id || item?.id || `enr-${idx}`);
                      const label = `${item?.schemeName || "Scheme"} - ${item?.productType || item?.planType || "-"}`;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              <div className="customer-dashboard-grid">
                <div className="customer-metric">
                  <div className="customer-metric-label">Plan Type</div>
                  <div className="customer-metric-value">{openValueMeta.productType || "-"}</div>
                </div>
                <div className="customer-metric">
                  <div className="customer-metric-label">Wallet Total</div>
                  <div className="customer-metric-value">Rs. {openValueMeta.totalAmount}</div>
                </div>
                <div className="customer-metric">
                  <div className="customer-metric-label">Redeemed</div>
                  <div className="customer-metric-value">Rs. {openValueMeta.redeemedAmount}</div>
                </div>
                <div className="customer-metric">
                  <div className="customer-metric-label">Available Credit</div>
                  <div className="customer-metric-value">Rs. {openValueMeta.availableCredit}</div>
                </div>
                <div className="customer-metric customer-metric-status">
                  <div className="customer-metric-label">Redemption Status</div>
                  <div className="customer-metric-value">{openValueMeta.redemptionStatus}</div>
                </div>
              </div>
            </div>

            <div className="customer-grid" style={{ marginTop: 14 }}>
              <div className="customer-card customer-form">
                <h3>Add Products</h3>
                <div className="customer-field">
                  <label>Product Type</label>
                  <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                    <option value="INDIVIDUAL">INDIVIDUAL</option>
                    <option value="COMBO">COMBO</option>
                  </select>
                </div>
                <div className="customer-field">
                  <label>Product</label>
                  <select value={selectedCode} onChange={(e) => setSelectedCode(e.target.value)}>
                    <option value="">Select Product</option>
                    {filteredCatalog.map((item) => (
                      <option key={`${item.type}-${item.code}`} value={item.code}>
                        {`${item.name} (${item.code}) - Rs. ${item.price}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="customer-field">
                  <label>Quantity</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </div>
                <div className="customer-row">
                  <button className="customer-btn customer-btn-primary" type="button" onClick={addToCart}>Add Item</button>
                </div>
              </div>

              <div className="customer-card customer-form">
                <h3>Redemption Cart</h3>
                {cart.length === 0 ? (
                  <div className="customer-helper-text">No items added yet.</div>
                ) : (
                  <div className="customer-table-wrap">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Code</th>
                          <th>Qty</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart.map((item, index) => (
                          <tr key={`${item.type}-${item.code}-${index}`}>
                            <td>{item.type}</td>
                            <td>{item.code}</td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={item.quantity}
                                onChange={(e) => updateCartQuantity(index, e.target.value)}
                                style={{ width: 70 }}
                              />
                            </td>
                            <td>Rs. {toNumber(item.price) * toNumber(item.quantity)}</td>
                            <td>
                              <button className="customer-btn customer-btn-secondary" type="button" onClick={() => removeCartItem(index)}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="customer-helper-text" style={{ marginTop: 10 }}>
                  Cart Total: Rs. {cartTotal}
                </div>
                <div className="customer-helper-text">
                  Available Credit: Rs. {openValueMeta.availableCredit}
                </div>
                <div className="customer-helper-text" style={{ fontWeight: 600 }}>
                  Estimated Cash Top-up: Rs. {topUpEstimate}
                </div>
                <div className="customer-field" style={{ marginTop: 10 }}>
                  <label>Notes</label>
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
                <div className="customer-row">
                  <button
                    className="customer-btn customer-btn-primary"
                    type="button"
                    disabled={!isOpenValuePlan || !openValueMeta.canRedeem || submitLoading || cart.length === 0}
                    onClick={submitRedemption}
                  >
                    {submitLoading ? "Submitting..." : "Confirm Redemption"}
                  </button>
                </div>
                {!isOpenValuePlan && (
                  <div className="customer-error" style={{ marginTop: 10 }}>
                    Redemption is only available for Savings Scheme enrollments.
                  </div>
                )}
                {isOpenValuePlan && !openValueMeta.canRedeem && (
                  <div className="customer-error" style={{ marginTop: 10 }}>
                    Redemption is locked for this enrollment. Complete required installments first.
                  </div>
                )}
              </div>
            </div>

            <div className="customer-card customer-table-wrap" style={{ marginTop: 14 }}>
              <h3>Redemption History</h3>
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Total Redeemed</th>
                    <th>Before</th>
                    <th>After</th>
                    <th>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptionHistory.length === 0 ? (
                    <tr><td colSpan="5">No redemption history found.</td></tr>
                  ) : (
                    redemptionHistory.map((item, index) => {
                      const lineItems = item?.lineItems || item?.products || [];
                      const itemsLabel = Array.isArray(lineItems)
                        ? lineItems
                            .map((line) => `${line?.code || "-"} x${line?.quantity || 1}`)
                            .join(", ")
                        : "-";

                      return (
                        <tr key={item?._id || item?.id || index}>
                          <td>{item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</td>
                          <td>Rs. {toNumber(item?.totalRedeemedAmount || item?.amount)}</td>
                          <td>Rs. {toNumber(item?.balanceBefore)}</td>
                          <td>Rs. {toNumber(item?.balanceAfter)}</td>
                          <td>{itemsLabel || "-"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {error && <div className="customer-error" style={{ marginTop: 12 }}>{error}</div>}
            {success && <div className="customer-success" style={{ marginTop: 12 }}>{success}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default SavingsRedemptionPage;
